// The workloads family as the console speaks it: the placement records a
// console pane follows (02-DESIGN/04-workloads/04-placement-records.md § the
// family and the stream, § who reads) — the executor publishes
// `placements.<tenant>.<workload>.<kind>.<executor>`, the service its own
// events under `service`, a record is attributed by its subject and the
// placement header, and the member and admin templates admit
// a subscription on the tenant's own token; the tenant is the family's own
// second token, not an account slot — and the admin's verb that asks for one
// of the platform's own workloads by name
// (02-DESIGN/04-workloads/01-the-workload-model.md § the verbs; decision 0018).
import { request } from "./request.js";
import { address, client, Family } from "./subject.js";
/** SERVICE is the workload service's micro service name, under which callers discover its sealing key. */
export const SERVICE = "workloads";
/** The three kinds of placement record. */
export const RecordKind = {
    /** One line the placement wrote, with its stream and when. */
    output: "output",
    /** The runtime's metrics at an instant. */
    sample: "sample",
    /** A lifecycle transition. */
    event: "event",
};
/** PLACEMENT_HEADER names the placement a record is attributed to. */
export const PLACEMENT_HEADER = "Impire-Placement";
/**
 * RECORD_WRITER_SERVICE is the writer token of the workload service's own
 * events; every other record names the executor that wrote it (decision 0025).
 */
export const RECORD_WRITER_SERVICE = "service";
/**
 * recordSubject is `placements.<tenant>.<workload>.<kind>.<writer>`: one
 * workload's records of one kind by one writer — an executor's id, or
 * RECORD_WRITER_SERVICE.
 */
export function recordSubject(tenant, workload, kind, writer) {
    return client(Family.placements, tenant, workload, kind, writer);
}
/** recordKindFilter is `placements.<tenant>.<workload>.<kind>.*`: one workload's records of one kind, every writer. */
export function recordKindFilter(tenant, workload, kind) {
    return client(Family.placements, tenant, workload, kind, "*");
}
/** recordWriter reads the writer back from a record's subject; undefined for one that is not a record's. */
export function recordWriter(subj) {
    let toks;
    try {
        toks = address(subj);
    }
    catch {
        return undefined;
    }
    return toks.length === 5 && toks[0] === Family.placements ? toks[4] : undefined;
}
/**
 * heldBy reports whether a record is one a reader keeps: written by the
 * service, or by the executor that holds the workload's current placement
 * (02-DESIGN/04-workloads/04-placement-records.md § only the holder's records
 * are read; decision 0025).
 */
export function heldBy(subj, executor) {
    const writer = recordWriter(subj);
    return writer === RECORD_WRITER_SERVICE || (writer !== undefined && executor !== "" && writer === executor);
}
/** recordFilter is `placements.<tenant>.<workload>.>`: every record of one workload. */
export function recordFilter(tenant, workload) {
    return client(Family.placements, tenant, workload, ">");
}
/**
 * VERB_REGISTER_PLATFORM registers one of the platform's own workloads in
 * the tenant by its name, the workload service filling the image, the key
 * and the staged secrets from the name (decision 0018): the admin's verb the
 * Stores surface calls to bring up the tenant's state service.
 */
export const VERB_REGISTER_PLATFORM = "register-platform";
/** PlatformName is the name of one of the platform's own workloads. */
export const PlatformName = {
    vault: "vault",
    state: "state",
    observer: "observer",
};
/** validPlatformName reports whether s is one of the three. */
export function validPlatformName(s) {
    return s === PlatformName.vault || s === PlatformName.state || s === PlatformName.observer;
}
/** registerPlatformSubject is `workloads.register-platform.<name>`. */
export function registerPlatformSubject(name) {
    if (!validPlatformName(name)) {
        throw new Error(`workloads: "${name}" is not a platform workload`);
    }
    return client(Family.workloads, VERB_REGISTER_PLATFORM, name);
}
/**
 * registerPlatform asks for one of the platform's own workloads by name, with
 * an empty body — the console names an image, a key or a path never — and
 * answers the workload id; the service refuses a tenant that already has one
 * of that name, or an install that ships no image under it. The headers given
 * ride beside the sealing header: the carrier.
 */
export function registerPlatform(nc, caller, name, opts) {
    return request(nc, caller, SERVICE, registerPlatformSubject(name), undefined, opts);
}
//# sourceMappingURL=workloads.js.map